import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { useHostProject } from '@/hooks/useSwr';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import {
  STUDIO_BASE_URL_LOCAL_STORAGE_KEY,
  STUDIO_HEADERS_LOCAL_STORAGE_KEY,
} from '../constants';
import { useCreateStudioClient } from './useCreateStudioClient';

export const useIntializeStudioClient = ({
  hostProjectId,
}: {
  hostProjectId: string | null;
}) => {
  const [searchParams] = useSearchParams();
  const baseUrlFromSearchParams = searchParams.get('baseUrl') ?? undefined;

  const [localBaseUrl, setLocalBaseUrl] = useLocalStorageState(
    STUDIO_BASE_URL_LOCAL_STORAGE_KEY,
    undefined,
    z.string()
  );

  const [localHeaders] = useLocalStorageState(
    STUDIO_HEADERS_LOCAL_STORAGE_KEY,
    {},
    z.record(z.string(), z.string())
  );

  const [hostProjectHeaders] = useLocalStorageState(
    `${STUDIO_HEADERS_LOCAL_STORAGE_KEY}:${hostProjectId}`,
    {},
    z.record(z.string(), z.string())
  );

  const { data: project, isLoading } = useHostProject(hostProjectId);

  // If the baseUrl is not set in local storage, set it from the search params
  useEffect(() => {
    if (!localBaseUrl && baseUrlFromSearchParams) {
      setLocalBaseUrl(baseUrlFromSearchParams);
    }
  }, [localBaseUrl, baseUrlFromSearchParams, setLocalBaseUrl]);

  const clientParams = useMemo(() => {
    if (hostProjectId) {
      const baseUrl = project != null ? project.resource?.url : undefined;
      return {
        projectId: hostProjectId,
        baseUrl,
        headers: hostProjectHeaders,
      };
    }
    return {
      baseUrl: baseUrlFromSearchParams,
      headers: localHeaders,
    };
  }, [
    hostProjectId,
    project,
    localHeaders,
    baseUrlFromSearchParams,
    hostProjectHeaders,
  ]);

  const client = useCreateStudioClient(clientParams);
  return { client, isLoadingClient: isLoading };
};
